import Crypto from 'crypto-js';

// Small helper to safely read values from localStorage, including ones
// serialized by Quasar's LocalStorage plugin (which prefixes values like
// "__q_strn|", "__q_objt|", etc.).

function stripQuasarPrefix(val) {
  if (typeof val !== 'string') return val;
  if (!val.startsWith('__q_')) return val;
  const idx = val.indexOf('|');
  if (idx === -1) return val; // unexpected format; return as-is
  return val.slice(idx + 1);
}

export function getLocalStorageParsed(key, defaultValue = null) {
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) return defaultValue;

    // Handle Quasar-prefixed values
    const maybeStripped = stripQuasarPrefix(raw);

    // Try JSON.parse first when it looks like JSON
    const looksJson = typeof maybeStripped === 'string' && (/^\s*[{[]/.test(maybeStripped));
    if (looksJson) {
      return JSON.parse(maybeStripped);
    }

    // Fallback: try JSON.parse anyway (covers primitive literals like true/false/123)
    try {
      return JSON.parse(maybeStripped);
    } catch (_) {
      // Last resort: return the raw string
      return maybeStripped;
    }
  } catch (e) {
    // If anything goes wrong, don't crash app during import time
    console.warn('[storage.util] Failed to parse localStorage key', key, e);
    return defaultValue;
  }
}

export function setLocalStorageJSON(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    console.warn('[storage.util] Failed to stringify localStorage key', key, e);
  }
}

// The student ID of the logged-in profile. The `pelajar` docs saved as
// localStorage.user do not always carry an `email` field, so decrypt the
// stored studentid (AES-encrypted with the profile uuid, see
// auth.service.js) and only fall back to email when that fails.
export function getStoredStudentId() {
  const user = getLocalStorageParsed('user');
  if (!user) return null;
  if (user.studentid && user.uuid) {
    try {
      const id = Crypto.AES.decrypt(user.studentid, user.uuid).toString(Crypto.enc.Utf8);
      if (id) return id;
    } catch (_) {
      // fall through to email
    }
  }
  return user.email ? user.email.split('@')[0] : null;
}
